import { Link } from "react-router-dom";
import { ArrowRight, Shield, Eye, AlertTriangle, BarChart3 } from "lucide-react";
import { motion } from "framer-motion";

const solutions = [
  {
    title: "Defence & Counter-UAS",
    subtitle: "Analysis, mission integration and autonomy",
    description: "RLK provides mission-systems and analytical technology for defence and counter-UAS programmes. From capability assessment with UAVSimNet to hardware-neutral mission integration and AI-AV autonomous control where required.",
    icon: Shield,
    href: "/solutions/defence-cuas",
    features: [
      "Counter-UAS capability assessment with UAVSimNet",
      "Hardware-neutral mission integration across sensors and effectors",
      "AI-AV integration for vision-based autonomous control",
      "Evidence packs, model governance and test planning",
    ],
  },
  {
    title: "Protective Security",
    subtitle: "Helais Sentinel for site protection",
    description: "Address the continuity problem: fixed systems can lose track or leave blind areas, while operators need controlled escalation and an auditable response. Helais Sentinel integrates with existing cameras and perimeter systems.",
    icon: Eye,
    href: "/solutions/protective-security",
    features: [
      "Integration with existing cameras and perimeter systems",
      "Qualified aerial observation as optional response asset",
      "Operator authorisation and evidence logging",
      "Pilot scope defined by site, sensor coverage and operational rules",
    ],
  },
  {
    title: "Public Safety & Emergency Response",
    subtitle: "Mission orchestration through Helais",
    description: "Helais provides mission orchestration, not just proprietary drones. Integrate incident workflows, coordinate multiple assets, and maintain human oversight with clear regulatory deployment boundaries.",
    icon: AlertTriangle,
    href: "/solutions/public-safety",
    features: [
      "Incident and mission workflow integration",
      "Multi-asset coordination and hand-off",
      "Support for qualified third-party UAVs and sensors",
      "Human oversight and regulatory deployment boundaries",
    ],
  },
  {
    title: "Civil Operational Intelligence",
    subtitle: "Analytics from existing infrastructure",
    description: "UrbanAnalytix delivers operational intelligence from your existing video and operational data. Convert observations into defined metrics, alerts or workflow outputs with clear data governance.",
    icon: BarChart3,
    href: "/solutions/civil-intelligence",
    features: [
      "Use existing video and operational data where possible",
      "Convert observations into metrics, alerts or workflow outputs",
      "Clear data governance, retention and integration requirements",
      "Bounded pilots with clear conversion criteria",
    ],
  },
];

export default function SolutionsPage() {
  return (
    <div className="relative">
      {/* Hero */}
      <section className="pt-32 pb-16 relative">
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-caption mb-4">Solutions</p>
            <h1 className="text-display text-foreground mb-6">
              Defined Customer Outcomes
            </h1>
            <p className="text-subtitle max-w-2xl">
              We solve defined operational problems through modular solution compositions. Each solution combines RLK technology with existing infrastructure and qualified partner equipment.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="section-padding-lg">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="space-y-8">
            {solutions.map((solution, index) => (
              <motion.article
                key={index}
                className="card-pillar p-8 lg:p-10"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="grid lg:grid-cols-2 gap-8 items-start">
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="h-10 w-10 rounded border border-border bg-muted/50 flex items-center justify-center">
                        <solution.icon className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <span className="text-caption">{solution.subtitle}</span>
                    </div>
                    <h2 className="text-title text-foreground mb-4">{solution.title}</h2>
                    <p className="text-body mb-6">{solution.description}</p>
                    <Link to={solution.href} className="btn-secondary">
                      Learn More
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Link>
                  </div>
                  <div>
                    <ul className="space-y-3">
                      {solution.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3 p-3 rounded bg-muted/30 border border-border/50">
                          <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
                          <span className="text-sm text-muted-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding-lg bg-card/30 edge-glow">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-headline text-foreground mb-6">
              Discuss Your Requirement
            </h2>
            <p className="text-body mb-8">
              Bring us the operational problem. We will define the smallest credible system needed to solve it.
            </p>
            <Link to="/contact" className="btn-primary">
              Request a Briefing
              <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
